import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";

import { batchList } from "../../../api/Batch";
import {
  discountOffersList,
  studentDiscountOfferApply,
  studentDiscountOfferApplyPost,
} from "../../../api/StudentDiscountOffer";

import { SweetAlertShow } from "../../master/Master";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import Master from "../Master";

/* ============================================================
   HELPERS
============================================================ */

const money = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const normalize = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const normalizeRegistration = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const normalizeFeeKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const trimPct = (value) => {
  const s = String(value ?? "0");
  return s.includes(".") ? s.replace(/0+$/, "").replace(/\.$/, "") : s;
};

/* ============================================================
   COMPONENT
============================================================ */

export default function StudentDiscountOfferApply() {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");

  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState("");

  const [discountOffers, setDiscountOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);

  const [students, setStudents] = useState([]);

  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const [updateStatus, setUpdateStatus] = useState({});

  /*
   Snapshot of the last-saved state per student, used to detect
   which rows actually changed (same pattern as StudentFeeUpdateV1)
  */
  const originalStudentsRef = useRef({});

  const excelInputRef = useRef(null);
  const [excelLoading, setExcelLoading] = useState(false);
  const [excelMatched, setExcelMatched] = useState(0);
  const [excelNotMatched, setExcelNotMatched] = useState([]);

  /* ==========================================================
     INITIAL SESSION
  ========================================================== */

  useEffect(() => {
    const storedSessions = JSON.parse(localStorage.getItem("sessions") ?? "[]");

    setSessions(storedSessions);

    const activeSession =
      storedSessions.find((s) => String(s.status) === "1") || storedSessions[0];

    if (activeSession?.id) {
      setSessionId(String(activeSession.id));
      getBatchList(activeSession.id);
    }
  }, []);

  /* ==========================================================
     GET BATCHES
  ========================================================== */

  const getBatchList = async (sid) => {
    if (!sid) return;

    setLoading(true);

    try {
      const response = await batchList(`limit=all&session_id=${sid}`);

      if (response?.data?.status) {
        setBatches(response.data.data || []);
      } else {
        setBatches([]);
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Failed to load batches",
        );
      }
    } catch (error) {
      setBatches([]);
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load batches",
      );
    } finally {
      setLoading(false);
    }
  };

  /* ==========================================================
     GET DISCOUNT OFFERS FOR THE SELECTED BATCH
  ========================================================== */

  const getDiscountOffers = async (selectedBatchId, sid) => {
    if (!selectedBatchId) return;

    setOffersLoading(true);

    try {
      const response = await discountOffersList(
        `batch_id=${selectedBatchId}&session_id=${sid}`,
      );

      if (response?.data?.status) {
        // Sort highest slab first so score-matching always prefers the
        // best-fitting offer when slabs are checked in order.
        const sorted = [...(response.data.data || [])].sort(
          (a, b) => num(b.min_percentage) - num(a.min_percentage),
        );

        setDiscountOffers(sorted);
      } else {
        setDiscountOffers([]);
      }
    } catch (error) {
      setDiscountOffers([]);
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load discount offers",
      );
    } finally {
      setOffersLoading(false);
    }
  };

  /* ==========================================================
     SESSION CHANGE
  ========================================================== */

  const handleSessionChange = async (value) => {
    setSessionId(value);
    setBatchId("");
    setStudents([]);
    setDiscountOffers([]);
    setUpdateStatus({});

    originalStudentsRef.current = {};

    await getBatchList(value);
  };

  /* ==========================================================
     BATCH CHANGE
  ========================================================== */

  const handleBatchChange = async (value) => {
    setBatchId(value);
    setStudents([]);
    setUpdateStatus({});

    originalStudentsRef.current = {};

    if (!value || !sessionId) return;

    await Promise.all([
      fetchStudents(value),
      getDiscountOffers(value, sessionId),
    ]);
  };

  /* ==========================================================
     FIND MATCHING OFFER BY SCORE
  ========================================================== */

  const findOfferByScore = (score, offers = discountOffers) => {
    const s = num(score);

    return (
      offers.find(
        (offer) =>
          s >= num(offer.min_percentage) && s <= num(offer.max_percentage),
      ) || null
    );
  };

  const findOfferById = (offerId, offers = discountOffers) =>
    offers.find((offer) => String(offer.id) === String(offerId)) || null;

  /* ==========================================================
     COMPUTE DISCOUNTED FEES FOR AN OFFER

     - percentage offers: applied per eligible fee line
     - amount offers: applied once, to the first eligible line
       (normally Tuition Fee) since a flat rupee amount can't be
       meaningfully split across multiple fee types
  ========================================================== */
  const NON_DISCOUNTABLE_FEE_TYPES = [
    "Registration Fee",
    "Previous Session Fees",
  ];

  const applyOfferToFees = (fees, offer) => {
    if (!offer) {
      return fees.map((fee) => {
        const actual = num(fee.actual_amount);
        const paid = num(fee.paid_amount);

        return {
          ...fee,
          final_amount: actual,
          discount: 0,
          due_amount: Math.max(actual - paid, 0),
        };
      });
    }

    const totalFee = fees.reduce((sum, fee) => sum + num(fee.actual_amount), 0);

    let discountValue = 0;

    if (offer.discount_type === "percentage") {
      discountValue = Math.min(num(offer.discount_percentage), 100);
    } else {
      discountValue = Math.min(num(offer.discount_amount), totalFee);
    }

    const expectedFinal =
      offer.discount_type === "percentage"
        ? totalFee - (totalFee * discountValue) / 100
        : totalFee - discountValue;

    const expectedFinalRounded = Math.max(0, Math.round(expectedFinal));

    let updatedFees = fees.map((fee) => {
      const amount = num(fee.actual_amount);
      const paid = num(fee.paid_amount);

      if (NON_DISCOUNTABLE_FEE_TYPES.includes(fee.fee_type)) {
        return {
          ...fee,
          final_amount: amount,
          discount: 0,
          due_amount: Math.max(amount - paid, 0),
        };
      }

      let finalAmount = amount;

      if (offer.discount_type === "percentage") {
        finalAmount = amount - (amount * discountValue) / 100;
      } else {
        // Fixed discount distributed proportionally
        const ratio = totalFee > 0 ? amount / totalFee : 0;

        finalAmount = amount - discountValue * ratio;
      }

      finalAmount = Math.round(finalAmount);

      // Never make payable less than already paid
      finalAmount = Math.max(paid, finalAmount);

      return {
        ...fee,
        final_amount: finalAmount,
        discount: Math.max(amount - finalAmount, 0),
        due_amount: Math.max(finalAmount - paid, 0),
      };
    });

    // Adjust rounding difference exactly like FeeDetailModal
    let currentTotal = updatedFees.reduce(
      (sum, fee) => sum + num(fee.final_amount),
      0,
    );

    let diff = expectedFinalRounded - currentTotal;

    if (diff !== 0) {
      for (let i = updatedFees.length - 1; i >= 0; i--) {
        if (!NON_DISCOUNTABLE_FEE_TYPES.includes(updatedFees[i].fee_type)) {
          const adjusted = num(updatedFees[i].final_amount) + diff;

          updatedFees[i].final_amount = Math.max(
            num(updatedFees[i].paid_amount),
            adjusted,
          );

          updatedFees[i].discount = Math.max(
            num(updatedFees[i].actual_amount) -
              num(updatedFees[i].final_amount),
            0,
          );

          updatedFees[i].due_amount = Math.max(
            num(updatedFees[i].final_amount) - num(updatedFees[i].paid_amount),
            0,
          );

          break;
        }
      }
    }

    return updatedFees;
  };

  /* ==========================================================
     PREPARE STUDENT
  ========================================================== */

  const prepareStudent = (student) => {
    const existingFees = student?.student_fee_details || [];

    let fees = existingFees.map((fee) => {
      const actual = num(fee.actual_amount);
      const paid = num(fee.paid_amount);
      const finalAmount = num(fee.final_amount ?? actual);

      return {
        student_fee_id: fee.id || null,
        fee_type: fee.fee_type,
        actual_amount: actual,
        paid_amount: paid,
        discount: num(fee.discount ?? Math.max(actual - finalAmount, 0)),
        final_amount: Math.max(finalAmount, paid),
        due_amount: Math.max(num(fee.due_amount ?? finalAmount - paid), 0),
      };
    });

    return {
      ...student,
      fees,
      last_year_score: "",
      discount_offer_id: null,
      discount_offer_title: "",
      update_status: null,
      update_message: "",
    };
  };

  /* ==========================================================
     FETCH STUDENTS
  ========================================================== */

  const fetchStudents = async (selectedBatchId) => {
    setLoading(true);

    try {
      const response = await studentDiscountOfferApply(
        `batch_id=${selectedBatchId}`,
      );

      if (!response?.data?.status) {
        setStudents([]);
        originalStudentsRef.current = {};
        return SweetAlertShow(
          "info",
          "No Students",
          response?.data?.message || "No enrolled students found.",
        );
      }

      const data = response?.data?.data || [];

      if (!data.length) {
        setStudents([]);
        originalStudentsRef.current = {};
        return SweetAlertShow(
          "info",
          "No Students",
          "No enrolled students found.",
        );
      }

      const prepared = data.map((student) => prepareStudent(student));

      setStudents(prepared);

      const snapshot = {};
      prepared.forEach((student) => {
        snapshot[student.id] = createComparableStudent(student);
      });

      originalStudentsRef.current = snapshot;

      setUpdateStatus({});
      setExcelMatched(0);
      setExcelNotMatched([]);
    } catch (error) {
      setStudents([]);
      originalStudentsRef.current = {};

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load students",
      );
    } finally {
      setLoading(false);
    }
  };

  /* ==========================================================
     COMPARABLE STUDENT (for dirty detection)
  ========================================================== */

  const createComparableStudent = (student) => ({
    discount_offer_id: student.discount_offer_id || null,
    last_year_score: String(student.last_year_score || ""),
    fees: (student.fees || [])
      .map((fee) => ({
        id: fee.student_fee_id || null,
        fee_type: fee.fee_type,
        final_amount: num(fee.final_amount),
      }))
      .sort((a, b) => String(a.fee_type).localeCompare(String(b.fee_type))),
  });

  const isStudentDirty = (student) => {
    const original = originalStudentsRef.current[student.id];
    if (!original) return true;

    const current = createComparableStudent(student);
    return JSON.stringify(current) !== JSON.stringify(original);
  };

  const dirtyStudents = useMemo(
    () => students.filter((student) => isStudentDirty(student)),
    [students],
  );

  const getTotalPaid = (student) =>
    (student.fees || []).reduce((sum, fee) => sum + num(fee.paid_amount), 0);

  const calculateTotals = (student) => {
    const actual = (student.fees || []).reduce(
      (s, f) => s + num(f.actual_amount),
      0,
    );
    const paid = (student.fees || []).reduce(
      (s, f) => s + num(f.paid_amount),
      0,
    );
    const discount = (student.fees || []).reduce(
      (s, f) => s + num(f.discount),
      0,
    );
    const payable = (student.fees || []).reduce(
      (s, f) => s + num(f.final_amount),
      0,
    );
    const due = (student.fees || []).reduce((s, f) => s + num(f.due_amount), 0);

    return { actual, paid, discount, payable, due };
  };

  /* ==========================================================
     SCORE CHANGE -> AUTO MATCH OFFER
  ========================================================== */

  const handleScoreChange = (studentIndex, value) => {
    const score = value.replace(/[^0-9.]/g, "");

    setStudents((prev) =>
      prev.map((student, index) => {
        if (index !== studentIndex) return student;

        const matched = findOfferByScore(score);

        return {
          ...student,
          last_year_score: score,
          discount_offer_id: matched?.id || null,
          discount_offer_title: matched?.title || "",
          fees: applyOfferToFees(student.fees, matched),
        };
      }),
    );
  };

  /* ==========================================================
     MANUAL OFFER SELECT
  ========================================================== */

  const handleOfferChange = (studentIndex, offerId) => {
    setStudents((prev) =>
      prev.map((student, index) => {
        if (index !== studentIndex) return student;

        if (!offerId) {
          return {
            ...student,
            discount_offer_id: null,
            discount_offer_title: "",
            fees: applyOfferToFees(student.fees, null),
          };
        }

        const offer = findOfferById(offerId);

        return {
          ...student,
          discount_offer_id: offer?.id || null,
          discount_offer_title: offer?.title || "",
          fees: applyOfferToFees(student.fees, offer),
        };
      }),
    );
  };

  /* ==========================================================
     CLEAR OFFER FOR ONE STUDENT
  ========================================================== */

  const clearOffer = (studentIndex) => {
    setStudents((prev) =>
      prev.map((student, index) =>
        index === studentIndex
          ? {
              ...student,
              last_year_score: "",
              discount_offer_id: null,
              discount_offer_title: "",
              fees: applyOfferToFees(student.fees, null),
            }
          : student,
      ),
    );
  };

  /* ==========================================================
     BULK: APPLY BEST-MATCH OFFER TO EVERY UNPAID STUDENT
     WHO ALREADY HAS A SCORE ENTERED
  ========================================================== */

  const applyMatchToAllScored = () => {
    if (!students.length) return;

    let appliedCount = 0;

    setStudents((prev) =>
      prev.map((student) => {
        if (!student.last_year_score) return student;

        const matched = findOfferByScore(student.last_year_score);
        if (!matched) return student;

        appliedCount += 1;

        return {
          ...student,
          discount_offer_id: matched.id,
          discount_offer_title: matched.title,
          fees: applyOfferToFees(student.fees, matched),
        };
      }),
    );

    SweetAlertShow(
      appliedCount ? "success" : "info",
      appliedCount ? "Offers Matched" : "No Match",
      appliedCount
        ? `${appliedCount} students matched to an offer by score. Click Apply All to save.`
        : "No entered scores matched an active offer slab.",
    );
  };

  /* ==========================================================
     BUILD API PAYLOAD
  ========================================================== */

  const buildPayload = (student) => ({
    student_batch_n_fee_id: student.id,
    discount_offer_id: student.discount_offer_id || null,
    last_year_score: student.last_year_score || null,
    fee_type: student.fees.map((fee) => fee.fee_type),
    total: student.fees.map((fee) => num(fee.actual_amount)),
    final: student.fees.map((fee) => num(fee.final_amount)),
  });

  /* ==========================================================
     VALIDATE STUDENT
  ========================================================== */

  const validateStudent = (student) => {
    const errors = [];

    student.fees.forEach((fee) => {
      const actual = num(fee.actual_amount);
      const paid = num(fee.paid_amount);
      const payable = num(fee.final_amount);

      if (payable < paid) {
        errors.push(
          `${fee.fee_type}: Payable ₹${money(payable)} cannot be less than paid ₹${money(paid)}.`,
        );
      }

      if (payable > actual) {
        errors.push(`${fee.fee_type}: Payable fee cannot exceed actual fee.`);
      }
    });

    return errors;
  };

  /* ==========================================================
     MARK STUDENT SAVED
  ========================================================== */

  const markStudentAsSaved = (student) => {
    originalStudentsRef.current[student.id] = createComparableStudent(student);
  };

  /* ==========================================================
     APPLY SINGLE STUDENT
  ========================================================== */

  const applyToStudent = async (studentIndex) => {
    const student = students[studentIndex];
    if (!student) return;

    if (!isStudentDirty(student)) {
      return SweetAlertShow(
        "info",
        "No Changes",
        "There are no changes to apply for this student.",
      );
    }

    const errors = validateStudent(student);

    if (errors.length) {
      return SweetAlertShow("error", "Validation Error", errors.join("\n"));
    }

    setUpdating(true);

    try {
      const payload = buildPayload(student);
      console.log(`payload`);

      const response = await studentDiscountOfferApplyPost(payload);

      if (response?.data?.status) {
        markStudentAsSaved(student);

        setUpdateStatus((prev) => ({
          ...prev,
          [studentIndex]: {
            status: "success",
            message: response?.data?.message || "Applied successfully",
          },
        }));

        SweetAlertShow(
          "success",
          "Applied",
          `${student.student?.name || "Student"} updated successfully.`,
        );

        if (response?.data?.data) {
          const updatedStudent = prepareStudent(response.data.data);

          setStudents((prev) =>
            prev.map((item, index) =>
              index === studentIndex ? updatedStudent : item,
            ),
          );

          originalStudentsRef.current[updatedStudent.id] =
            createComparableStudent(updatedStudent);
        }
      } else {
        throw new Error(response?.data?.message || "Apply failed");
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.errors ||
        error?.message ||
        "Apply failed";

      setUpdateStatus((prev) => ({
        ...prev,
        [studentIndex]: {
          status: "error",
          message: Array.isArray(message)
            ? message.join(", ")
            : String(message),
        },
      }));

      SweetAlertShow(
        "error",
        "Apply Failed",
        Array.isArray(message) ? message.join(", ") : String(message),
      );
    } finally {
      setUpdating(false);
    }
  };

  /* ==========================================================
     APPLY ALL CHANGED (chunked, only dirty students)
  ========================================================== */

  const applyAll = async () => {
    if (!students.length) return;

    const changedStudents = students.filter((student) =>
      isStudentDirty(student),
    );

    if (!changedStudents.length) {
      return SweetAlertShow(
        "info",
        "No Changes",
        "No student has any unsaved changes.",
      );
    }

    const invalid = [];

    changedStudents.forEach((student) => {
      const errors = validateStudent(student);
      if (errors.length) invalid.push({ student, errors });
    });

    if (invalid.length) {
      const first = invalid[0];
      return SweetAlertShow(
        "error",
        "Validation Error",
        `${first.student.student?.name || "Student"}: ${first.errors.join(", ")}`,
      );
    }

    setUpdating(true);

    const status = {};

    try {
      const chunkSize = 10;

      for (let i = 0; i < changedStudents.length; i += chunkSize) {
        const chunk = changedStudents.slice(i, i + chunkSize);

        await Promise.allSettled(
          chunk.map(async (student) => {
            const index = students.findIndex((item) => item.id === student.id);

            try {
              const payload = buildPayload(student);
              const response = await studentDiscountOfferApplyPost(payload);

              if (response?.data?.status) {
                status[index] = {
                  status: "success",
                  message: response?.data?.message || "Applied",
                };

                markStudentAsSaved(student);
              } else {
                status[index] = {
                  status: "error",
                  message: response?.data?.message || "Apply failed",
                };
              }
            } catch (error) {
              status[index] = {
                status: "error",
                message:
                  error?.response?.data?.message ||
                  error?.message ||
                  "Apply failed",
              };
            }
          }),
        );

        setUpdateStatus({ ...status });
      }

      const success = Object.values(status).filter(
        (x) => x.status === "success",
      ).length;
      const failed = Object.values(status).filter(
        (x) => x.status === "error",
      ).length;

      SweetAlertShow(
        failed === 0 ? "success" : "warning",
        "Apply Completed",
        `Changed: ${changedStudents.length}, Applied: ${success}, Failed: ${failed}`,
      );

      if (failed === 0) {
        await fetchStudents(batchId);
      }
    } finally {
      setUpdating(false);
    }
  };

  /* ==========================================================
     EXCEL SCORE IMPORT

     Expected columns:
       registration_no   e.g. 2705063
       score / last_year_score / percentage   e.g. 92.5

     For each matched row:
       - skip entirely if the student already has any payment
         (same rule StudentFeeUpdateV1 uses for RTE import)
       - otherwise set last_year_score and auto-apply the matching offer
  ========================================================== */

  const handleExcelScoreUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!students.length) {
      event.target.value = "";
      return SweetAlertShow(
        "warning",
        "Select Batch",
        "Please load students first.",
      );
    }

    if (!discountOffers.length) {
      event.target.value = "";
      return SweetAlertShow(
        "warning",
        "No Offers",
        "No active discount offers found for this batch.",
      );
    }

    setExcelLoading(true);

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

      if (!rows.length) {
        throw new Error("Excel file is empty.");
      }

      const firstRow = rows[0];

      const registrationKey = Object.keys(firstRow).find((key) => {
        const k = normalize(key).replace(/[\s_-]/g, "");
        return [
          "registrationno",
          "registrationnumber",
          "registration",
          "regno",
          "admissionno",
        ].includes(k);
      });

      const scoreKey = Object.keys(firstRow).find((key) => {
        const k = normalize(key).replace(/[\s_-]/g, "");
        return [
          "score",
          "lastyearscore",
          "percentage",
          "marks",
          "percent",
        ].includes(k);
      });

      if (!registrationKey) {
        throw new Error(
          "Registration number column not found. Use 'registration_no' in Excel.",
        );
      }

      if (!scoreKey) {
        throw new Error(
          "Score column not found. Use 'score' (or 'percentage') in Excel.",
        );
      }

      const scoreByRegistration = new Map();

      rows.forEach((row) => {
        const registration = normalizeRegistration(row[registrationKey]);
        const score = num(row[scoreKey]);

        if (registration) {
          scoreByRegistration.set(registration, score);
        }
      });

      let matched = 0;
      const notMatched = [];

      setStudents((prev) =>
        prev.map((student) => {
          const registration = normalizeRegistration(
            student.student?.registration_no,
          );

          if (!scoreByRegistration.has(registration)) {
            return student;
          }

          const totalPaid = getTotalPaid(student);

          if (totalPaid > 0) {
            notMatched.push(
              `${student.student?.name || "Unknown"} - payment exists`,
            );
            return student;
          }

          const score = scoreByRegistration.get(registration);
          const offer = findOfferByScore(score);

          if (!offer) {
            notMatched.push(
              `${student.student?.name || "Unknown"} - no matching offer for ${score}%`,
            );
            return {
              ...student,
              last_year_score: String(score),
            };
          }

          matched += 1;

          return {
            ...student,
            last_year_score: String(score),
            discount_offer_id: offer.id,
            discount_offer_title: offer.title,
            fees: applyOfferToFees(student.fees, offer),
          };
        }),
      );

      const loadedRegistrations = new Set(
        students.map((student) =>
          normalizeRegistration(student.student?.registration_no),
        ),
      );

      scoreByRegistration.forEach((_score, registration) => {
        if (!loadedRegistrations.has(registration)) {
          notMatched.push(registration);
        }
      });

      setExcelMatched(matched);
      setExcelNotMatched(notMatched);

      SweetAlertShow(
        matched > 0 ? "success" : "warning",
        "Excel Processed",
        `${matched} students matched to an offer by score. Click Apply All to save changes.`,
      );
    } catch (error) {
      SweetAlertShow(
        "error",
        "Excel Error",
        error?.message || "Unable to process Excel file.",
      );
    } finally {
      setExcelLoading(false);
      event.target.value = "";
    }
  };

  /* ==========================================================
     BATCH TOTALS
  ========================================================== */

  const batchTotals = useMemo(() => {
    return students.reduce(
      (total, student) => {
        const t = calculateTotals(student);

        total.actual += t.actual;
        total.paid += t.paid;
        total.discount += t.discount;
        total.payable += t.payable;
        total.due += t.due;

        return total;
      },
      { actual: 0, paid: 0, discount: 0, payable: 0, due: 0 },
    );
  }, [students]);

  /* ==========================================================
     RENDER
  ========================================================== */

  return (
    <>
      {loading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{ background: "rgba(0,0,0,.25)", zIndex: 9999 }}
        >
          <div className="spinner-border text-primary" />
        </div>
      )}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <Master />

                  <div className="col-12">
                    {/* FILTER CARD */}
                    <div className="card mb-3">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div>
                            <h5 className="mb-1">
                              Student Discount Offer Apply
                            </h5>
                            <small className="text-muted">
                              Match last-year score against active offer slabs,
                              or select an offer manually
                            </small>
                          </div>
                        </div>

                        <div className="row g-3">
                          <div className="col-md-3">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={sessionId}
                                onChange={(e) =>
                                  handleSessionChange(e.target.value)
                                }
                              >
                                <option value="">Select Session</option>
                                {sessions.map((session) => (
                                  <option key={session.id} value={session.id}>
                                    {session.session}
                                    {String(session.status) === "1"
                                      ? " (Active)"
                                      : ""}
                                  </option>
                                ))}
                              </select>
                              <label>Session</label>
                            </div>
                          </div>

                          <div className="col-md-4">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={batchId}
                                onChange={(e) =>
                                  handleBatchChange(e.target.value)
                                }
                                disabled={!sessionId}
                              >
                                <option value="">Select Batch</option>
                                {batches.map((batch) => (
                                  <option key={batch.id} value={batch.id}>
                                    {batch.title}
                                    {batch.course?.title
                                      ? ` (${batch.course.title})`
                                      : ""}
                                  </option>
                                ))}
                              </select>
                              <label>Batch</label>
                            </div>
                          </div>

                          <div className="col-md-2">
                            <div className="card bg-light border mb-0 h-100">
                              <div className="card-body py-2">
                                <small className="text-muted">Students</small>
                                <h4 className="mb-0">{students.length}</h4>
                                {dirtyStudents.length > 0 && (
                                  <small className="text-warning">
                                    {dirtyStudents.length} changed
                                  </small>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="col-md-3 d-flex align-items-center justify-content-end">
                            <button
                              className="btn btn-success btn-lg"
                              onClick={applyAll}
                              disabled={updating || !dirtyStudents.length}
                            >
                              {updating ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" />
                                  Applying...
                                </>
                              ) : (
                                <>
                                  <i className="ri-price-tag-3-line me-1" />
                                  Apply All
                                  {dirtyStudents.length > 0 &&
                                    ` (${dirtyStudents.length})`}
                                </>
                              )}
                            </button>
                          </div>
                        </div>

                        {/* ACTIVE OFFERS PREVIEW */}
                        {!!batchId && (
                          <>
                            <hr />
                            <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                              <strong>Active Offers:</strong>
                              {offersLoading && (
                                <span className="spinner-border spinner-border-sm" />
                              )}
                              {!offersLoading && !discountOffers.length && (
                                <span className="text-muted small">
                                  No active discount offers for this batch.
                                </span>
                              )}
                              {discountOffers.map((offer) => (
                                <span
                                  key={offer.id}
                                  className="badge bg-label-primary border"
                                >
                                  {offer.title}: {trimPct(offer.min_percentage)}
                                  %-
                                  {trimPct(offer.max_percentage)}% →{" "}
                                  {offer.discount_type === "percentage"
                                    ? `${trimPct(offer.discount_percentage)}% off`
                                    : `₹${money(offer.discount_amount)} off`}
                                </span>
                              ))}
                            </div>

                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <strong>Bulk Score Match:</strong>

                              <button
                                className="btn btn-outline-success"
                                onClick={applyMatchToAllScored}
                                disabled={
                                  !students.length ||
                                  !discountOffers.length ||
                                  updating
                                }
                              >
                                <i className="ri-check-double-line me-1" />
                                Match Entered Scores
                              </button>

                              <button
                                className="btn btn-outline-primary"
                                onClick={() => excelInputRef.current?.click()}
                                disabled={
                                  !students.length ||
                                  !discountOffers.length ||
                                  excelLoading ||
                                  updating
                                }
                              >
                                {excelLoading ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Processing Excel...
                                  </>
                                ) : (
                                  <>
                                    <i className="ri-file-excel-2-line me-1" />
                                    Import Score Excel
                                  </>
                                )}
                              </button>

                              <input
                                ref={excelInputRef}
                                type="file"
                                accept=".xlsx,.xls,.csv"
                                className="d-none"
                                onChange={handleExcelScoreUpload}
                              />

                              <small className="text-muted">
                                Excel must contain{" "}
                                <strong>registration_no</strong> and{" "}
                                <strong>score</strong> columns.
                              </small>
                            </div>

                            {excelMatched > 0 && (
                              <div className="alert alert-success mt-3 mb-0">
                                <strong>Excel:</strong> {excelMatched} students
                                matched to an offer. Click{" "}
                                <strong>Apply All</strong> to save.
                              </div>
                            )}

                            {excelNotMatched.length > 0 && (
                              <div className="alert alert-warning mt-3 mb-0">
                                <strong>Not matched / skipped:</strong>{" "}
                                {excelNotMatched.length} students.
                                <div
                                  className="mt-2"
                                  style={{
                                    maxHeight: "100px",
                                    overflowY: "auto",
                                  }}
                                >
                                  {excelNotMatched.slice(0, 50).join(", ")}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* SUMMARY */}
                    {students.length > 0 && (
                      <div className="row g-2 mb-3">
                        <div className="col">
                          <div className="card border">
                            <div className="card-body py-2">
                              <small className="text-muted">Actual Fees</small>
                              <h6 className="mb-0">
                                ₹{money(batchTotals.actual)}
                              </h6>
                            </div>
                          </div>
                        </div>
                        <div className="col">
                          <div className="card border">
                            <div className="card-body py-2">
                              <small className="text-muted">Paid</small>
                              <h6 className="mb-0 text-success">
                                ₹{money(batchTotals.paid)}
                              </h6>
                            </div>
                          </div>
                        </div>
                        <div className="col">
                          <div className="card border">
                            <div className="card-body py-2">
                              <small className="text-muted">Discount</small>
                              <h6 className="mb-0 text-warning">
                                ₹{money(batchTotals.discount)}
                              </h6>
                            </div>
                          </div>
                        </div>
                        <div className="col">
                          <div className="card border">
                            <div className="card-body py-2">
                              <small className="text-muted">Payable</small>
                              <h6 className="mb-0">
                                ₹{money(batchTotals.payable)}
                              </h6>
                            </div>
                          </div>
                        </div>
                        <div className="col">
                          <div className="card border">
                            <div className="card-body py-2">
                              <small className="text-muted">Due</small>
                              <h6 className="mb-0 text-danger">
                                ₹{money(batchTotals.due)}
                              </h6>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* STUDENT TABLE */}
                    <div className="card">
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <div>
                          <h5 className="mb-0">Students</h5>
                          {batchId && (
                            <small className="text-muted">
                              {
                                batches.find(
                                  (b) => String(b.id) === String(batchId),
                                )?.title
                              }
                            </small>
                          )}
                        </div>

                        {students.length > 0 && (
                          <div className="d-flex gap-2">
                            <span className="badge bg-primary">
                              {students.length} Students
                            </span>
                            {dirtyStudents.length > 0 && (
                              <span className="badge bg-warning text-dark">
                                {dirtyStudents.length} Unsaved
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="card-body p-0">
                        {!students.length ? (
                          <div className="text-center py-5">
                            <i
                              className="ri-price-tag-3-line text-muted"
                              style={{ fontSize: "50px" }}
                            />
                            <h5 className="mt-3">Select Session &amp; Batch</h5>
                            <p className="text-muted mb-0">
                              Students will automatically appear here.
                            </p>
                          </div>
                        ) : (
                          <div
                            className="table-responsive"
                            style={{ maxHeight: "calc(100vh - 300px)" }}
                          >
                            <table className="table table-bordered table-hover align-middle mb-0">
                              <thead
                                className="table-dark"
                                style={{
                                  position: "sticky",
                                  top: 0,
                                  zIndex: 10,
                                }}
                              >
                                <tr>
                                  <th>#</th>
                                  <th style={{ minWidth: 220 }}>Student</th>
                                  <th style={{ minWidth: 130 }}>
                                    Last Year Score
                                  </th>
                                  <th style={{ minWidth: 220 }}>
                                    Discount Offer
                                  </th>
                                  <th style={{ minWidth: 180 }}>Total</th>
                                  <th>Action</th>
                                </tr>
                              </thead>

                              <tbody>
                                {students.map((student, studentIndex) => {
                                  const totals = calculateTotals(student);
                                  const totalPaid = totals.paid;
                                  const dirty = isStudentDirty(student);
                                  const status = updateStatus[studentIndex];

                                  return (
                                    <tr
                                      key={student.id}
                                      className={dirty ? "table-warning" : ""}
                                    >
                                      <td>
                                        <strong>{studentIndex + 1}</strong>
                                        {dirty && (
                                          <span
                                            className="badge bg-warning text-dark d-block mt-1"
                                            style={{ fontSize: "9px" }}
                                          >
                                            UNSAVED
                                          </span>
                                        )}
                                      </td>

                                      <td>
                                        <div className="fw-semibold">
                                          {student.student?.name || "-"}
                                        </div>
                                        <div className="small text-muted">
                                          Reg:{" "}
                                          {student.student?.registration_no ||
                                            "-"}
                                        </div>
                                        {totalPaid > 0 && (
                                          <span className="badge bg-success mt-1">
                                            Paid ₹{money(totalPaid)}
                                          </span>
                                        )}
                                      </td>

                                      <td>
                                        <div className="input-group input-group-sm">
                                          <input
                                            type="text"
                                            className="form-control"
                                            placeholder="e.g. 92.5"
                                            value={student.last_year_score}
                                            disabled={totalPaid > 0}
                                            onChange={(e) =>
                                              handleScoreChange(
                                                studentIndex,
                                                e.target.value,
                                              )
                                            }
                                          />
                                          <span className="input-group-text">
                                            %
                                          </span>
                                        </div>
                                        {totalPaid > 0 && (
                                          <small className="text-danger d-block mt-1">
                                            Locked: payment exists
                                          </small>
                                        )}
                                      </td>

                                      <td>
                                        <select
                                          className={`form-select form-select-sm ${
                                            student.discount_offer_id
                                              ? "border-success bg-success-subtle"
                                              : ""
                                          }`}
                                          value={
                                            student.discount_offer_id || ""
                                          }
                                          disabled={totalPaid > 0}
                                          onChange={(e) =>
                                            handleOfferChange(
                                              studentIndex,
                                              e.target.value,
                                            )
                                          }
                                        >
                                          <option value="">No Offer</option>
                                          {discountOffers.map((offer) => (
                                            <option
                                              key={offer.id}
                                              value={offer.id}
                                            >
                                              {offer.title} (
                                              {trimPct(offer.min_percentage)}%-
                                              {trimPct(offer.max_percentage)}%)
                                            </option>
                                          ))}
                                        </select>

                                        {student.discount_offer_id && (
                                          <button
                                            className="btn btn-link btn-sm text-danger p-0 mt-1"
                                            onClick={() =>
                                              clearOffer(studentIndex)
                                            }
                                            disabled={totalPaid > 0}
                                          >
                                            Clear Offer
                                          </button>
                                        )}
                                      </td>

                                      <td>
                                        <div className="small">
                                          <div className="d-flex justify-content-between">
                                            <span>Actual</span>
                                            <strong>
                                              ₹{money(totals.actual)}
                                            </strong>
                                          </div>
                                          <div className="d-flex justify-content-between text-success">
                                            <span>Paid</span>
                                            <strong>
                                              ₹{money(totals.paid)}
                                            </strong>
                                          </div>
                                          <div className="d-flex justify-content-between text-warning">
                                            <span>Discount</span>
                                            <strong>
                                              ₹{money(totals.discount)}
                                            </strong>
                                          </div>
                                          <hr className="my-1" />
                                          <div className="d-flex justify-content-between">
                                            <span>Payable</span>
                                            <strong>
                                              ₹{money(totals.payable)}
                                            </strong>
                                          </div>
                                          <div className="d-flex justify-content-between text-danger">
                                            <span>Due</span>
                                            <strong>
                                              ₹{money(totals.due)}
                                            </strong>
                                          </div>
                                        </div>
                                      </td>

                                      <td>
                                        <button
                                          className={`btn btn-sm w-100 ${dirty ? "btn-primary" : "btn-outline-secondary"}`}
                                          onClick={() =>
                                            applyToStudent(studentIndex)
                                          }
                                          disabled={updating || !dirty}
                                        >
                                          <i className="ri-price-tag-3-line me-1" />
                                          {dirty ? "Apply" : "Saved"}
                                        </button>

                                        {status && (
                                          <div className="mt-2">
                                            {status.status === "success" ? (
                                              <span className="badge bg-success">
                                                Applied
                                              </span>
                                            ) : (
                                              <>
                                                <span className="badge bg-danger">
                                                  Failed
                                                </span>
                                                <div
                                                  className="text-danger mt-1"
                                                  style={{ fontSize: "11px" }}
                                                >
                                                  {status.message}
                                                </div>
                                              </>
                                            )}
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
